import { For, Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js'

import HabitTagPauseMenu from '../components/habits/HabitTagPauseMenu'
import HabitTagsField from '../components/habits/HabitTagsField'
import HabitTimeBlocks from '../components/habits/HabitTimeBlocks'
import HabitTaskCard, { type HabitPlacement } from '../components/habits/HabitTaskCard'
import Button from '../components/ui/Button'
import FieldLabel from '../components/ui/FieldLabel'
import Modal from '../components/ui/Modal'
import MultiSelect from '../components/ui/MultiSelect'
import Select from '../components/ui/Select'
import TextInput from '../components/ui/TextInput'
import { useI18n } from '../i18n'
import { viewPanelState } from '../state/appUiStore'
import {
   addHabit,
   completeHabit,
   deleteHabit,
   HABIT_COMPLETION_ANIMATION_MS,
   type Habit,
   habitAlsoCompletesInput,
   getLocalTimeInputValue,
   habitDueDateInput,
   habitDueTimeInput,
   habitKindInput,
   habitNameInput,
   habitScheduleDaysInput,
   habitScheduleRuleInput,
   habitTagsInput,
   habits,
   hydrateHabitsFromStorage,
   isHabitDue,
   SCHEDULE_RULE_PRESETS,
   setHabitAlsoCompletesInput,
   setHabitDueDateInput,
   setHabitDueTimeInput,
   setHabitKindInput,
   setHabitNameInput,
   setHabitScheduleDaysInput,
   setHabitScheduleRuleInput,
   setHabitTagsInput,
   subscribeHabitsToStorageChanges,
   toggleHabitPaused,
   updateHabitDetails,
} from '../state/habitsStore'
import { createLiveClock } from '../utils/liveClock'

const HABIT_STATUS_REFRESH_MS = 60 * 1000

type PinnedCompletedHabit = {
   habit: Habit
   orderedHabitIds: string[]
   placement: HabitPlacement
}

/**
 * Returns a local date-input value (`YYYY-MM-DD`) for a timestamp.
 *
 * @param timestamp - The epoch-ms timestamp (defaults to now).
 * @returns The date-input string.
 */
function getLocalDateInputValue(timestamp = Date.now()) {
   const date = new Date(timestamp)
   return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate(),
   ).padStart(2, '0')}`
}

/**
 * Sorts habits to match a pinned id order (keeps a just-completed habit in place).
 *
 * @param nextHabits - The habits to sort.
 * @param orderedHabitIds - The desired id order.
 * @returns A new sorted array.
 */
function sortHabitsByPinnedOrder(nextHabits: Habit[], orderedHabitIds: string[]) {
   const orderIndexById = new Map(
      orderedHabitIds.map((habitId, index) => [habitId, index]),
   )
   return [...nextHabits].sort((a, b) => {
      const aIndex = orderIndexById.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const bIndex = orderIndexById.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return aIndex - bIndex
   })
}

/**
 * Sorts due habits: recurring ones first, then calendar tasks, then one-off tasks.
 *
 * @param nextHabits - The habits to sort.
 * @returns A new sorted array.
 */
function sortCurrentUnfulfilledHabits(nextHabits: Habit[]) {
   const byKind = (kind: Habit['kind']) => nextHabits.filter(habit => habit.kind === kind)
   return [...byKind('recurring'), ...byKind('calendar'), ...byKind('one-off')]
}

type HabitFormFieldsProps = {
   prefix: string
   nameValue: string
   kindValue: Habit['kind']
   scheduleDaysValue: string
   scheduleRuleValue: string
   dueDateValue: string
   dueTimeValue: string
   alsoCompletesValue: string[]
   alsoCompletesOptions: Pick<Habit, 'id' | 'name'>[]
   tagsValue: string[]
   namePlaceholder?: string
   nameRef?: (element: HTMLInputElement) => void
   onNameInput: (value: string) => void
   onKindInput: (value: Habit['kind']) => void
   onScheduleDaysInput: (value: string) => void
   onScheduleRuleInput: (value: string) => void
   onDueDateInput: (value: string) => void
   onDueTimeInput: (value: string) => void
   onAlsoCompletesToggle: (habitId: string) => void
   onTagsChange: (tags: string[]) => void
}

const CUSTOM_RULE_OPTION = 'custom'

/**
 * Renders the task-type toggle (repeating, calendar, one-off).
 *
 * @param props - The selected kind and change handler.
 * @returns The toggle element.
 */
const HabitKindToggle = (props: {
   value: Habit['kind']
   onInput: (value: Habit['kind']) => void
}) => {
   const t = useI18n()
   const kinds = [
      { kind: 'recurring', label: 'Repeating' },
      { kind: 'calendar', label: 'Calendar' },
      { kind: 'one-off', label: 'One-off' },
   ] as const
   return (
      <div>
         <FieldLabel>{t('Task type')}</FieldLabel>
         <div class="grid grid-cols-3 gap-2">
            <For each={kinds}>
               {item => (
                  <button
                     type="button"
                     class="rounded-lg border px-3 py-2 text-sm font-semibold transition"
                     classList={{
                        'border-emerald-400 bg-emerald-500/15 text-emerald-100':
                           props.value === item.kind,
                        'border-edge bg-surface-control text-ink-secondary':
                           props.value !== item.kind,
                     }}
                     onClick={() => props.onInput(item.kind)}
                  >
                     {t(item.label)}
                  </button>
               )}
            </For>
         </div>
      </div>
   )
}

/**
 * Renders a date input with a time input beside it.
 *
 * @param props - Field ids, label, values, and change handlers.
 * @returns The fields element.
 */
const DateAndTimeFields = (props: {
   prefix: string
   dateLabel: string
   dateValue: string
   timeValue: string
   onDateInput: (value: string) => void
   onTimeInput: (value: string) => void
}) => {
   const t = useI18n()
   return (
      <div class="grid grid-cols-[1fr_auto] gap-2">
         <div>
            <FieldLabel for={`${props.prefix}-habit-due-date`}>
               {props.dateLabel}
            </FieldLabel>
            <TextInput
               id={`${props.prefix}-habit-due-date`}
               type="date"
               value={props.dateValue}
               onInput={event => props.onDateInput(event.currentTarget.value)}
            />
         </div>
         <div>
            <FieldLabel for={`${props.prefix}-habit-due-time`}>{t('Time')}</FieldLabel>
            <TextInput
               id={`${props.prefix}-habit-due-time`}
               type="time"
               value={props.timeValue}
               onInput={event => props.onTimeInput(event.currentTarget.value)}
            />
         </div>
      </div>
   )
}

/**
 * Renders the calendar schedule picker: a preset repeat, or a custom RRULE.
 *
 * @param props - Field id prefix, the current rule, and its change handler.
 * @returns The fields element.
 */
const ScheduleRuleFields = (props: {
   prefix: string
   value: string
   onInput: (value: string) => void
}) => {
   const t = useI18n()
   const isPreset = (rule: string) =>
      SCHEDULE_RULE_PRESETS.some(item => item.rule === rule)
   const [customMode, setCustomMode] = createSignal(!isPreset(props.value))
   return (
      <>
         <div>
            <FieldLabel for={`${props.prefix}-habit-schedule-rule`}>
               {t('Repeats')}
            </FieldLabel>
            <Select
               id={`${props.prefix}-habit-schedule-rule`}
               value={customMode() ? CUSTOM_RULE_OPTION : props.value}
               onChange={event => {
                  const value = event.currentTarget.value
                  setCustomMode(value === CUSTOM_RULE_OPTION)
                  if (value !== CUSTOM_RULE_OPTION) {
                     props.onInput(value)
                  }
               }}
            >
               <For each={SCHEDULE_RULE_PRESETS}>
                  {item => <option value={item.rule}>{t(item.label)}</option>}
               </For>
               <option value={CUSTOM_RULE_OPTION}>{t('Custom')}</option>
            </Select>
         </div>
         <Show when={customMode()}>
            <div>
               <FieldLabel for={`${props.prefix}-habit-custom-rule`}>
                  {t('Custom rule (RRULE)')}
               </FieldLabel>
               <TextInput
                  id={`${props.prefix}-habit-custom-rule`}
                  value={props.value}
                  placeholder="FREQ=MONTHLY;BYDAY=1MO"
                  spellcheck={false}
                  onInput={event => props.onInput(event.currentTarget.value)}
               />
            </div>
         </Show>
      </>
   )
}

/**
 * Renders the shared habit form fields (name, kind, schedule/due date, tags).
 *
 * @param props - The form field props.
 * @returns The fields element.
 */
const HabitFormFields = (props: HabitFormFieldsProps) => {
   const t = useI18n()
   /**
    * Returns whether the selected kind is recurring.
    *
    * @returns True for recurring habits.
    */
   const isRecurring = () => props.kindValue === 'recurring'
   /**
    * Returns the label for the date field: a due date or a calendar task's first date.
    *
    * @returns The translated label.
    */
   const dateLabel = () =>
      props.kindValue === 'calendar' ? t('First date') : t('Due date')

   return (
      <>
         <div>
            <FieldLabel for={`${props.prefix}-habit-name`}>{t('Habit name')}</FieldLabel>
            <TextInput
               ref={props.nameRef}
               id={`${props.prefix}-habit-name`}
               value={props.nameValue}
               onInput={event => props.onNameInput(event.currentTarget.value)}
               placeholder={props.namePlaceholder}
            />
         </div>
         <HabitKindToggle value={props.kindValue} onInput={props.onKindInput} />
         <Show when={isRecurring()}>
            <div>
               <FieldLabel for={`${props.prefix}-habit-schedule-days`}>
                  {t('Repeat after days')}
               </FieldLabel>
               <TextInput
                  id={`${props.prefix}-habit-schedule-days`}
                  type="number"
                  min="1"
                  max="365"
                  value={props.scheduleDaysValue}
                  onInput={event => props.onScheduleDaysInput(event.currentTarget.value)}
               />
            </div>
            <Show when={props.alsoCompletesOptions.length > 0}>
               <div>
                  <FieldLabel for={`${props.prefix}-habit-also-completes`}>
                     {t('Also completes')}
                  </FieldLabel>
                  <p class="mb-2 text-xs text-ink-faint">
                     {t('Completing this habit also completes the selected habits.')}
                  </p>
                  <MultiSelect
                     id={`${props.prefix}-habit-also-completes`}
                     options={props.alsoCompletesOptions}
                     selectedIds={props.alsoCompletesValue}
                     placeholder={t('Search habits')}
                     emptyLabel={t('No matching habits')}
                     removeLabel={name => t('Remove {name}', { name })}
                     onToggle={props.onAlsoCompletesToggle}
                  />
               </div>
            </Show>
         </Show>
         <Show when={!isRecurring()}>
            <DateAndTimeFields
               prefix={props.prefix}
               dateLabel={dateLabel()}
               dateValue={props.dueDateValue}
               timeValue={props.dueTimeValue}
               onDateInput={props.onDueDateInput}
               onTimeInput={props.onDueTimeInput}
            />
         </Show>
         <Show when={props.kindValue === 'calendar'}>
            <ScheduleRuleFields
               prefix={props.prefix}
               value={props.scheduleRuleValue}
               onInput={props.onScheduleRuleInput}
            />
         </Show>
         <HabitTagsField
            prefix={props.prefix}
            value={props.tagsValue}
            onChange={props.onTagsChange}
         />
      </>
   )
}

/**
 * Builds one placement list (due or undue) from the live habits, keeping
 * just-completed habits pinned in place until their animation finishes.
 *
 * @param currentHabits - All habits.
 * @param pinnedHabits - Habits pinned by an in-flight completion.
 * @param placement - Which list to build.
 * @param belongsHere - Whether an unpinned habit belongs in this list.
 * @returns The habits for the list, in display order.
 */
function buildPlacementList(
   currentHabits: Habit[],
   pinnedHabits: PinnedCompletedHabit[],
   placement: HabitPlacement,
   belongsHere: (habit: Habit) => boolean,
) {
   const pinnedById = new Map(pinnedHabits.map(pinned => [pinned.habit.id, pinned]))
   const nextHabits = currentHabits.flatMap(habit => {
      const pinnedHabit = pinnedById.get(habit.id)
      if (pinnedHabit) {
         return pinnedHabit.placement === placement ? [pinnedHabit.habit] : []
      }
      return belongsHere(habit) ? [habit] : []
   })
   const pinnedHere = pinnedHabits.filter(pinned => pinned.placement === placement)
   if (pinnedHere.length === 0) {
      return nextHabits
   }
   for (const pinnedHabit of pinnedHere) {
      if (!nextHabits.some(habit => habit.id === pinnedHabit.habit.id)) {
         nextHabits.push(pinnedHabit.habit)
      }
   }
   return sortHabitsByPinnedOrder(nextHabits, pinnedHere[0]?.orderedHabitIds ?? [])
}

/**
 * Returns whether a habit should stay visible for a completion animation.
 *
 * @param completedHabit - The habit that was completed directly.
 * @param candidate - A visible habit that may be completed by the same action.
 * @returns True when the candidate should be pinned.
 */
function isPinnedByCompletion(completedHabit: Habit, candidate: Habit) {
   return (
      candidate.id === completedHabit.id ||
      (completedHabit.alsoCompletes.includes(candidate.id) &&
         candidate.kind === 'recurring' &&
         !candidate.paused)
   )
}

/**
 * Captures the habits (and their list order) that a completion should keep
 * in place while the celebration animation runs.
 *
 * @param habit - The habit being completed.
 * @param placement - Which list it was completed from.
 * @param dueHabits - The due list before completion.
 * @param undueHabits - The undue list before completion.
 * @returns The pinned habits.
 */
function buildPinnedCompletedHabits(
   habit: Habit,
   placement: HabitPlacement,
   dueHabits: Habit[],
   undueHabits: Habit[],
): PinnedCompletedHabit[] {
   const dueOrder = dueHabits.map(item => item.id)
   const undueOrder = undueHabits.map(item => item.id)
   const pinned: PinnedCompletedHabit[] = [
      ...dueHabits
         .filter(candidate => isPinnedByCompletion(habit, candidate))
         .map(candidate => ({
            habit: candidate,
            orderedHabitIds: dueOrder,
            placement: 'due' as const,
         })),
      ...undueHabits
         .filter(candidate => isPinnedByCompletion(habit, candidate))
         .map(candidate => ({
            habit: candidate,
            orderedHabitIds: undueOrder,
            placement: 'undue' as const,
         })),
   ]
   if (!pinned.some(pinnedHabit => pinnedHabit.habit.id === habit.id)) {
      pinned.push({
         habit,
         orderedHabitIds: placement === 'due' ? dueOrder : undueOrder,
         placement,
      })
   }
   return pinned
}

/**
 * Toggles a habit id inside an "also completes" selection list.
 *
 * @param selection - The current selection.
 * @param habitId - The habit id to toggle.
 * @returns The updated selection.
 */
function toggleAlsoCompletesId(selection: string[], habitId: string) {
   return selection.includes(habitId) ?
         selection.filter(id => id !== habitId)
      :  [...selection, habitId]
}

/**
 * Returns recurring habits selectable as "also completes" targets.
 *
 * @param excludeHabitId - Habit id to leave out (the one being edited).
 * @returns The selectable habits.
 */
function alsoCompletesOptions(excludeHabitId: string | null) {
   return habits().filter(
      habit => habit.kind === 'recurring' && habit.id !== excludeHabitId,
   )
}

/**
 * Holds the edit-habit modal's draft fields and open/close/save/delete actions.
 *
 * @param currentTime - The live clock, used to default a one-off due date.
 * @returns The editor state and actions.
 */
function createHabitEditor(currentTime: () => number) {
   const [editingHabitId, setEditingHabitId] = createSignal<string | null>(null)
   const [name, setName] = createSignal('')
   const [scheduleDays, setScheduleDays] = createSignal('1')
   const [kind, setKind] = createSignal<Habit['kind']>('recurring')
   const [dueDate, setDueDate] = createSignal(getLocalDateInputValue())
   const [dueTime, setDueTime] = createSignal('00:00')
   const [scheduleRule, setScheduleRule] = createSignal('FREQ=WEEKLY')
   const [alsoCompletes, setAlsoCompletes] = createSignal<string[]>([])
   const [tags, setTags] = createSignal<string[]>([])
   const editingHabit = createMemo(
      () => habits().find(habit => habit.id === editingHabitId()) ?? null,
   )

   /**
    * Opens the edit modal for a habit, pre-filling its fields.
    *
    * @param habit - The habit to edit.
    */
   const open = (habit: Habit) => {
      setEditingHabitId(habit.id)
      setName(habit.name)
      setScheduleDays(String(habit.scheduleDays))
      setKind(habit.kind)
      setDueDate(getLocalDateInputValue(habit.dueDate ?? currentTime()))
      setDueTime(habit.dueDate === null ? '00:00' : getLocalTimeInputValue(habit.dueDate))
      setScheduleRule(habit.scheduleRule ?? 'FREQ=WEEKLY')
      setAlsoCompletes(habit.alsoCompletes)
      setTags(habit.tags)
   }
   /**
    * Closes the edit modal and clears its fields.
    */
   const close = () => {
      setEditingHabitId(null)
      setName('')
      setScheduleDays('1')
      setKind('recurring')
      setDueDate(getLocalDateInputValue())
      setDueTime('00:00')
      setScheduleRule('FREQ=WEEKLY')
      setAlsoCompletes([])
      setTags([])
   }
   /**
    * Saves the edited habit and closes the modal on success.
    */
   const submit = () => {
      const habit = editingHabit()
      if (
         habit &&
         updateHabitDetails(habit.id, {
            name: name(),
            kind: kind(),
            scheduleDays: scheduleDays(),
            scheduleRule: scheduleRule(),
            dueDate: dueDate(),
            dueTime: dueTime(),
            alsoCompletes: alsoCompletes(),
            tags: tags(),
         })
      ) {
         close()
      }
   }
   /**
    * Deletes the habit being edited and closes the modal.
    */
   const remove = () => {
      const habit = editingHabit()
      if (habit) {
         deleteHabit(habit.id)
         close()
      }
   }
   /**
    * Pauses or resumes the habit being edited.
    */
   const togglePaused = () => {
      const habit = editingHabit()
      if (habit) {
         toggleHabitPaused(habit.id)
      }
   }

   return {
      editingHabitId,
      editingHabit,
      fields: {
         name,
         scheduleDays,
         scheduleRule,
         kind,
         dueDate,
         dueTime,
         alsoCompletes,
         tags,
      },
      setName,
      setScheduleDays,
      setScheduleRule,
      setKind,
      setDueDate,
      setDueTime,
      setTags,
      toggleAlsoCompletes: (habitId: string) =>
         setAlsoCompletes(selection => toggleAlsoCompletesId(selection, habitId)),
      open,
      close,
      submit,
      remove,
      togglePaused,
   }
}

/**
 * Renders the pause/resume icon for the edit modal.
 *
 * @param props - Whether the habit is paused.
 * @returns The icon element.
 */
const PauseResumeIcon = (props: { paused: boolean }) => (
   <Show
      when={props.paused}
      fallback={
         <svg aria-hidden="true" viewBox="0 0 24 24" class="h-5 w-5">
            <path
               d="M8 6v12M16 6v12"
               fill="none"
               stroke="currentColor"
               stroke-linecap="round"
               stroke-width="2.5"
            />
         </svg>
      }
   >
      <svg aria-hidden="true" viewBox="0 0 24 24" class="h-5 w-5">
         <path d="M8 5v14l11-7-11-7Z" fill="currentColor" />
      </svg>
   </Show>
)

/**
 * Renders the add-habit modal bound to the store's draft inputs.
 *
 * @param props - Close/submit callbacks.
 * @returns The modal element.
 */
const AddHabitModal = (props: { onClose: () => void; onSubmit: () => void }) => {
   const t = useI18n()
   let nameInputRef: HTMLInputElement | undefined
   onMount(() => {
      queueMicrotask(() => {
         nameInputRef?.focus()
      })
   })
   return (
      <Modal id="add-habit-modal" onDismiss={props.onClose}>
         <h2 class="text-2xl font-bold">{t('Add habit')}</h2>
         <form
            id="add-habit-form"
            class="space-y-4"
            onSubmit={event => {
               event.preventDefault()
               props.onSubmit()
            }}
         >
            <HabitFormFields
               prefix="add"
               nameValue={habitNameInput()}
               kindValue={habitKindInput()}
               scheduleDaysValue={habitScheduleDaysInput()}
               scheduleRuleValue={habitScheduleRuleInput()}
               dueDateValue={habitDueDateInput()}
               dueTimeValue={habitDueTimeInput()}
               alsoCompletesValue={habitAlsoCompletesInput()}
               alsoCompletesOptions={alsoCompletesOptions(null)}
               tagsValue={habitTagsInput()}
               namePlaceholder={t('Add a habit')}
               nameRef={element => {
                  nameInputRef = element
               }}
               onNameInput={setHabitNameInput}
               onKindInput={setHabitKindInput}
               onScheduleDaysInput={setHabitScheduleDaysInput}
               onScheduleRuleInput={setHabitScheduleRuleInput}
               onDueDateInput={setHabitDueDateInput}
               onDueTimeInput={setHabitDueTimeInput}
               onAlsoCompletesToggle={habitId =>
                  setHabitAlsoCompletesInput(selection =>
                     toggleAlsoCompletesId(selection, habitId),
                  )
               }
               onTagsChange={setHabitTagsInput}
            />
            <div class="mt-4 flex gap-4">
               <Button id="save-add-habit-btn" type="submit" class="flex-1">
                  {t('Add')}
               </Button>
               <Button
                  id="cancel-add-habit-btn"
                  variant="secondary"
                  class="flex-1"
                  onClick={props.onClose}
               >
                  {t('Cancel')}
               </Button>
            </div>
         </form>
      </Modal>
   )
}

/**
 * Renders the edit-habit modal bound to an editor.
 *
 * @param props - The editor.
 * @returns The modal element.
 */
const EditHabitModal = (props: { editor: ReturnType<typeof createHabitEditor> }) => {
   const t = useI18n()
   let nameInputRef: HTMLInputElement | undefined
   onMount(() => {
      queueMicrotask(() => {
         nameInputRef?.focus()
      })
   })
   const { editor } = props
   return (
      <Modal id="edit-habit-modal" onDismiss={editor.close}>
         <h2 class="text-2xl font-bold">{t('Edit habit')}</h2>
         <form
            id="edit-habit-form"
            class="space-y-4"
            onSubmit={event => {
               event.preventDefault()
               editor.submit()
            }}
         >
            <HabitFormFields
               prefix="edit"
               nameValue={editor.fields.name()}
               kindValue={editor.fields.kind()}
               scheduleDaysValue={editor.fields.scheduleDays()}
               scheduleRuleValue={editor.fields.scheduleRule()}
               dueDateValue={editor.fields.dueDate()}
               dueTimeValue={editor.fields.dueTime()}
               alsoCompletesValue={editor.fields.alsoCompletes()}
               alsoCompletesOptions={alsoCompletesOptions(editor.editingHabitId())}
               tagsValue={editor.fields.tags()}
               nameRef={element => {
                  nameInputRef = element
               }}
               onNameInput={editor.setName}
               onKindInput={editor.setKind}
               onScheduleDaysInput={editor.setScheduleDays}
               onScheduleRuleInput={editor.setScheduleRule}
               onDueDateInput={editor.setDueDate}
               onDueTimeInput={editor.setDueTime}
               onAlsoCompletesToggle={editor.toggleAlsoCompletes}
               onTagsChange={editor.setTags}
            />
            <div class="mt-4 grid gap-3">
               <div class="flex gap-4">
                  <Button id="save-edit-habit-btn" type="submit" class="flex-1">
                     {t('Save')}
                  </Button>
                  <Button
                     id="cancel-edit-habit-btn"
                     variant="secondary"
                     class="flex-1"
                     onClick={editor.close}
                  >
                     {t('Cancel')}
                  </Button>
               </div>
               <Button
                  id="pause-edit-habit-btn"
                  variant="warning"
                  class="flex items-center justify-center gap-2"
                  onClick={editor.togglePaused}
               >
                  <PauseResumeIcon paused={Boolean(editor.editingHabit()?.paused)} />
                  {editor.editingHabit()?.paused ? t('Resume') : t('Pause')}
               </Button>
               <Button
                  id="delete-edit-habit-btn"
                  variant="danger"
                  onClick={editor.remove}
               >
                  {t('Delete')}
               </Button>
            </div>
         </form>
      </Modal>
   )
}

/**
 * Whether a habit can be completed now: recurring ones any time, calendar ones once
 * their first date has arrived (later occurrences may be completed early), and
 * one-off tasks once due.
 *
 * @param habit - The habit.
 * @param now - The reference time.
 * @returns True when the complete button should be enabled.
 */
function canCompleteHabit(habit: Habit, now: number) {
   if (habit.kind === 'recurring') {
      return true
   }
   if (habit.kind === 'calendar') {
      return (habit.dueDate ?? habit.createdAt) <= now
   }
   return isHabitDue(habit, now)
}

/**
 * Renders a due/undue habit list.
 *
 * @param props - The habits and card callbacks.
 * @returns The list element.
 */
const HabitList = (props: {
   habits: Habit[]
   placement: HabitPlacement
   currentTime: number
   class: string
   onComplete: (habit: Habit, placement: HabitPlacement) => void
   onEdit: (habit: Habit) => void
}) => (
   <div class={props.class}>
      <For each={props.habits}>
         {habit => (
            <HabitTaskCard
               habit={habit}
               placement={props.placement}
               currentTime={props.currentTime}
               canComplete={!habit.paused && canCompleteHabit(habit, props.currentTime)}
               onComplete={props.onComplete}
               onEdit={props.onEdit}
            />
         )}
      </For>
   </div>
)

/**
 * Tracks habits pinned in place by an in-flight completion animation.
 *
 * @returns The pinned list and the completion action.
 */
function createCompletionPinning() {
   const [pinnedHabits, setPinnedHabits] = createSignal<PinnedCompletedHabit[]>([])
   /**
    * Completes a habit while pinning its position during the animation.
    *
    * @param habit - The habit.
    * @param placement - Whether it was in the due or undue list.
    * @param dueHabits - The due list before completion.
    * @param undueHabits - The undue list before completion.
    */
   const complete = (
      habit: Habit,
      placement: HabitPlacement,
      dueHabits: Habit[],
      undueHabits: Habit[],
   ) => {
      const nextPinned = buildPinnedCompletedHabits(
         habit,
         placement,
         dueHabits,
         undueHabits,
      )
      const pinnedHabitIds = new Set(nextPinned.map(pinnedHabit => pinnedHabit.habit.id))
      const withoutPinned = (current: PinnedCompletedHabit[]) =>
         current.filter(pinnedHabit => !pinnedHabitIds.has(pinnedHabit.habit.id))
      setPinnedHabits(current => [...withoutPinned(current), ...nextPinned])
      completeHabit(habit.id)
      window.setTimeout(
         () => setPinnedHabits(withoutPinned),
         HABIT_COMPLETION_ANIMATION_MS,
      )
   }
   return { pinnedHabits, complete }
}

/**
 * Derives the due and undue habit lists from the store, the pinned habits,
 * and the live clock.
 *
 * @param pinnedHabits - Habits pinned by an in-flight completion.
 * @param currentTime - The live clock.
 * @returns The two list memos.
 */
function createHabitLists(
   pinnedHabits: () => PinnedCompletedHabit[],
   currentTime: () => number,
) {
   const dueHabits = createMemo(() =>
      sortCurrentUnfulfilledHabits(
         buildPlacementList(
            habits(),
            pinnedHabits(),
            'due',
            habit => !habit.paused && isHabitDue(habit, currentTime()),
         ),
      ),
   )
   const undueHabits = createMemo(() =>
      buildPlacementList(
         habits(),
         pinnedHabits(),
         'undue',
         habit => habit.paused || !isHabitDue(habit, currentTime()),
      ),
   )
   return { dueHabits, undueHabits }
}

/**
 * Renders the habits heading, due summary, pause-by-tag menu, and add button.
 *
 * @param props - The due count and add handler.
 * @returns The header element.
 */
const HabitsHeader = (props: { dueCount: number; onAdd: () => void }) => {
   const t = useI18n()
   return (
      <div class="mb-4 flex items-center justify-between gap-3">
         <div>
            <h2 class="text-xl font-bold text-ink">{t('Habits')}</h2>
            <p class="text-sm text-ink-secondary">
               {props.dueCount > 0 ?
                  t('{count} habits due', { count: props.dueCount })
               :  t('All habits are on track')}
            </p>
         </div>
         <div class="flex items-center gap-2">
            <HabitTagPauseMenu />
            <button
               type="button"
               class="grid h-11 w-11 place-items-center rounded-full bg-emerald-500 text-ink-inverse shadow-lg shadow-emerald-950/30 transition hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
               title={t('Add habit')}
               aria-label={t('Add habit')}
               onClick={props.onAdd}
            >
               <svg aria-hidden="true" viewBox="0 0 24 24" class="h-6 w-6">
                  <path
                     d="M12 5v14M5 12h14"
                     fill="none"
                     stroke="currentColor"
                     stroke-linecap="round"
                     stroke-width="2.5"
                  />
               </svg>
            </button>
         </div>
      </div>
   )
}

/**
 * Renders the habits tab (due/undue lists, add/edit modals).
 *
 * @returns The habits view element.
 */
const HabitsView = () => {
   const t = useI18n()
   const [isAddHabitModalOpen, setAddHabitModalOpen] = createSignal(false)
   const [currentTime, refreshCurrentTime] = createLiveClock({
      intervalMs: HABIT_STATUS_REFRESH_MS,
      refreshAtMidnight: true,
   })
   const pinning = createCompletionPinning()
   const editor = createHabitEditor(currentTime)
   const { dueHabits, undueHabits } = createHabitLists(pinning.pinnedHabits, currentTime)

   onMount(() => {
      hydrateHabitsFromStorage()
      onCleanup(subscribeHabitsToStorageChanges())
   })

   /**
    * Adds a habit and closes the modal on success.
    */
   const submitAddHabit = () => {
      if (addHabit()) {
         refreshCurrentTime()
         setAddHabitModalOpen(false)
      }
   }
   /**
    * Completes a habit while pinning its position during the animation.
    *
    * @param habit - The habit.
    * @param placement - Whether it was in the due or undue list.
    */
   const completeHabitFromPlacement = (habit: Habit, placement: HabitPlacement) =>
      pinning.complete(habit, placement, dueHabits(), undueHabits())

   return (
      <div
         id="habits-view"
         class="view-panel w-full lg:col-span-2"
         data-view-state={viewPanelState('habits')}
      >
         <div class="w-full">
            <HabitsHeader
               dueCount={dueHabits().length}
               onAdd={() => setAddHabitModalOpen(true)}
            />

            <Show
               when={habits().length > 0}
               fallback={
                  <p class="mt-5 rounded-lg border border-dashed border-edge-soft p-5 text-center text-sm text-ink-muted">
                     {t('No habits yet')}
                  </p>
               }
            >
               <HabitList
                  habits={dueHabits()}
                  placement="due"
                  currentTime={currentTime()}
                  class="mt-4 grid gap-3"
                  onComplete={completeHabitFromPlacement}
                  onEdit={editor.open}
               />
            </Show>

            <HabitTimeBlocks />

            <Show when={undueHabits().length > 0}>
               <HabitList
                  habits={undueHabits()}
                  placement="undue"
                  currentTime={currentTime()}
                  class="mt-5 grid gap-3"
                  onComplete={completeHabitFromPlacement}
                  onEdit={editor.open}
               />
            </Show>
         </div>
         <Show when={isAddHabitModalOpen()}>
            <AddHabitModal
               onClose={() => setAddHabitModalOpen(false)}
               onSubmit={submitAddHabit}
            />
         </Show>
         <Show when={editor.editingHabit()}>
            <EditHabitModal editor={editor} />
         </Show>
      </div>
   )
}

export default HabitsView
